import { i18n } from '@/i18n';
import { Alert } from '@/services/alert.service';
import { formatFloat } from '@/utils/format-numbers';
import { carsService } from '@/api/cars.service';
import { MilageDeviationReason, type MilageExpectationResponseDto } from '@carvia/ros-client-types';
import type { Car } from '@/entities/cars/car.entity';
import type { Allocation } from '@/entities/allocations/allocation.entity';
import { computed, ref } from 'vue';
import { allocationsService } from '@/api/allocations.service';

export type MilageDeviationConfirmation = {
  comment?: string;
  isConfirmed: boolean;
  reason?: MilageDeviationReason;
};

export function useConfirmMilageDeviation() {
  const isLoading = ref(false);

  const confirmPreparation = async (
    car: Car,
    newMilage: number,
  ): Promise<MilageDeviationConfirmation> => {
    if (car.milage === newMilage) {
      return { isConfirmed: true, reason: undefined };
    }
    try {
      isLoading.value = true;
      const res = await carsService.getMilageExpectation(car.id, {
        proposedMilage: newMilage,
      });
      isLoading.value = false;
      const translationKey = res.includesTransfer
        ? 'milageDeviation.preparationTransfer'
        : 'milageDeviation.fixed';
      return confirm(translationKey, res);
    } finally {
      isLoading.value = false;
    }
  };

  const confirmHandover = async (
    allocation: Allocation,
    newMilage: number,
  ): Promise<MilageDeviationConfirmation> => {
    if (allocation.milageStart === newMilage) {
      return { isConfirmed: true, reason: undefined };
    }
    try {
      isLoading.value = true;
      const res = await allocationsService.getMilageExpectation(allocation.id, {
        mode: 'handover',
        proposedMilage: newMilage,
      });
      isLoading.value = false;
      const translationKey = res.includesTransfer
        ? 'milageDeviation.handoverTransfer'
        : 'milageDeviation.fixed';
      return confirm(translationKey, res);
    } finally {
      isLoading.value = false;
    }
  };

  const confirmReturn = async (
    allocation: Allocation,
    newMilageEnd: number,
  ): Promise<MilageDeviationConfirmation> => {
    if (allocation?.milageEnd === newMilageEnd) {
      return { isConfirmed: true, reason: undefined };
    }
    try {
      isLoading.value = true;
      const res = await allocationsService.getMilageExpectation(allocation.id, {
        mode: 'return',
        proposedMilage: newMilageEnd,
      });
      isLoading.value = false;
      return confirm('milageDeviation.return', res);
    } finally {
      isLoading.value = false;
    }
  };

  const confirm = async (
    translationKey: string,
    props: MilageExpectationResponseDto,
  ): Promise<MilageDeviationConfirmation> => {
    if (!props.needsConfirmation) {
      return { isConfirmed: true, reason: undefined };
    }
    const formattedProps: Record<string, string> = {};
    Object.entries(props).forEach(([key, value]) => {
      if (typeof value === 'number') {
        formattedProps[key] = formatFloat(value, 2);
      }
    });
    const { t } = i18n.global;
    const needsReason = translationKey === 'milageDeviation.fixed';
    const reasonOptions = Object.values(MilageDeviationReason).map((reason) => ({
      label: t(`milageDeviation.reasons.${reason}`),
      value: reason,
    }));
    const { isConfirmed, value: reason } = await Alert.fire<MilageDeviationReason | undefined>({
      icon: 'warning',
      titleText: t('milageDeviation.warningTitle'),
      html: t(translationKey, formattedProps),
      showCancelButton: true,
      confirmButtonText: t('milageDeviation.saveAnyway'),
      cancelButtonText: t('cancel'),
      ...(needsReason
        ? {
            input: 'select',
            inputOptions: reasonOptions,
            inputLabel: t('milageDeviation.reasonLabel'),
            inputValidator: (value?: string) => {
              if (!value) {
                return t('milageDeviation.reasonRequired');
              }
              return null;
            },
          }
        : {}),
    });
    if (!isConfirmed || !reason || reason !== MilageDeviationReason.OTHER) {
      return { isConfirmed, reason };
    }
    const { isConfirmed: isCommentConfirmed, value: comment } = await Alert.fire<string>({
      icon: 'warning',
      titleText: t('milageDeviation.commentTitle'),
      html: t('milageDeviation.commentDescription'),
      input: 'textarea',
      inputLabel: t('milageDeviation.commentLabel'),
      inputPlaceholder: t('milageDeviation.commentPlaceholder'),
      showCancelButton: true,
      confirmButtonText: t('milageDeviation.saveAnyway'),
      cancelButtonText: t('cancel'),
      inputValidator: (value?: string) => {
        if (!value?.trim()) {
          return t('milageDeviation.commentRequired');
        }
        return null;
      },
    });
    return {
      isConfirmed: isCommentConfirmed,
      reason,
      comment: isCommentConfirmed ? comment.trim() : undefined,
    };
  };

  return {
    confirmReturn,
    confirmPreparation,
    confirmHandover,
    isLoading: computed(() => isLoading.value),
  };
}
